/**
 * OneDrive File Attachments — per-site configuration for the shared file library
 * that agents can browse and share links from during chat conversations.
 *
 * Architecture:
 *   - Uses the existing M365 Graph consent (Sites.ReadWrite.All)
 *   - Admin picks a SharePoint document library once
 *   - Agents browse it in the workspace chat window and paste sharing links into chat
 *   - Links are anonymous-view by default (configurable to org-scoped)
 *   - No per-agent OAuth required — shared library, admin-configured
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import useAdminFeatures from "@/hooks/useAdminFeatures";

interface M365Status { connected: boolean; tenantId?: string; }

interface SharePointSite { id: string; displayName: string; webUrl: string; }
interface Drive { id: string; name: string; driveType: string; webUrl: string; }

interface Settings {
  configured: boolean;
  libraryName: string;
  libraryUrl: string;
  graphSiteId: string;
  driveId: string;
  allowAgentUpload: boolean;
}

export default function OneDriveSettings() {
  const api = useApi();
  const { enableOneDriveAttachments } = useAdminFeatures();
  const [m365, setM365] = useState<M365Status>({ connected: false });
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);

  // Site/drive picker state
  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null);
  const [drives, setDrives] = useState<Drive[]>([]);
  const [loadingDrives, setLoadingDrives] = useState(false);
  const [selectedDrive, setSelectedDrive] = useState<Drive | null>(null);

  // Form state
  const [libraryName, setLibraryName] = useState("");
  const [allowUpload, setAllowUpload] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m365Res, settingsRes] = await Promise.all([
        api.admin.get("MicrosoftGraph/Status"),
        api.admin.get("MicrosoftGraph/OneDriveSettings")
      ]);
      const m365Data = await m365Res.json();
      const settingsData = await settingsRes.json();
      setM365(m365Data);
      setSettings(settingsData);
      if (settingsData.configured) {
        setLibraryName(settingsData.libraryName ?? "");
        setAllowUpload(settingsData.allowAgentUpload ?? false);
      }
    } catch {
      setM365({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function loadSites() {
    setLoadingSites(true);
    setSites([]);
    setDrives([]);
    setSelectedSite(null);
    setSelectedDrive(null);
    try {
      const res = await api.admin.get("MicrosoftGraph/Sites");
      const data = await res.json();
      setSites(Array.isArray(data) ? data : []);
    } catch { setSites([]); } finally { setLoadingSites(false); }
  }

  async function selectSite(site: SharePointSite) {
    setSelectedSite(site);
    setDrives([]);
    setSelectedDrive(null);
    setLoadingDrives(true);
    try {
      const res = await api.admin.get(`MicrosoftGraph/Drives?graphSiteId=${encodeURIComponent(site.id)}`);
      const data = await res.json();
      setDrives(Array.isArray(data) ? data : []);
    } catch { setDrives([]); } finally { setLoadingDrives(false); }
  }

  async function save() {
    if (!selectedSite || !selectedDrive) {
      setSaveMsg({ ok: false, text: "Please select a SharePoint site and document library first." });
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await api.admin.put("MicrosoftGraph/OneDriveSettings", {
        libraryName: libraryName || selectedDrive.name,
        libraryUrl: selectedSite.webUrl,
        graphSiteId: selectedSite.id,
        driveId: selectedDrive.id,
        allowAgentUpload: allowUpload
      });
      if (res.ok) {
        setSaveMsg({ ok: true, text: "File library saved. Agents can now browse it in the chat window." });
        await load();
        setSites([]);
        setSelectedSite(null);
        setSelectedDrive(null);
        setDrives([]);
      } else {
        setSaveMsg({ ok: false, text: "Save failed." });
      }
    } catch {
      setSaveMsg({ ok: false, text: "Save failed. Please try again." });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading...</div>;

  if (!enableOneDriveAttachments) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
          <p className="text-sm font-semibold text-amber-800 mb-1">OneDrive File Attachments — Upgrade Required</p>
          <p className="text-sm text-amber-700 mb-3">
            OneDrive file attachments are not included in your current plan.
            Contact support to enable this feature.
          </p>
          <Link to="/integrations/microsoft-365" className="text-sm text-blue-600 hover:underline">
            ← Back to Microsoft 365
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600">
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.25 14.69a5.25 5.25 0 0 0-4.5-8.19 7.5 7.5 0 0 0-14.25 3A4.5 4.5 0 0 0 4.5 18h15a3.75 3.75 0 0 0 .75-3.31Z"/>
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">OneDrive File Attachments</h1>
            <p className="text-sm text-slate-500">
              Let agents browse and share files from a shared SharePoint library during chat conversations.
            </p>
          </div>
        </div>
        <Link to="/integrations/microsoft-365" className="text-sm text-blue-600 hover:underline">
          ← Microsoft 365
        </Link>
      </div>

      {/* M365 connection check */}
      {!m365.connected && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6">
          <p className="text-sm font-medium text-amber-800 mb-1">Microsoft 365 not connected</p>
          <p className="text-xs text-amber-700 mb-3">
            OneDrive file attachments require a Microsoft 365 connection. Connect first — it takes about 30 seconds.
          </p>
          <Link to="/integrations/microsoft-365">
            <PrimaryBlueButton label="Connect Microsoft 365 →" onClick={() => {}} />
          </Link>
        </div>
      )}

      {/* Current config */}
      {settings?.configured && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 mb-5">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-green-600 text-sm">✓</span>
            <p className="text-sm font-medium text-green-800">File library configured</p>
          </div>
          <p className="text-xs text-green-700">
            Library: <strong>{settings.libraryName || "Unnamed"}</strong>
            {" · "}{settings.libraryUrl}
          </p>
          <p className="text-xs text-green-600 mt-0.5">
            Agent uploads: {settings.allowAgentUpload ? "enabled" : "disabled (agents can browse and share links only)"}
          </p>
        </div>
      )}

      {/* Setup / change library */}
      {m365.connected && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6">
          <p className="text-sm font-semibold text-slate-700 mb-4">
            {settings?.configured ? "Change file library" : "Configure file library"}
          </p>

          <HowItWorks />

          {/* Library name */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Library display name (shown to agents)
            </label>
            <input
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g. Team Files, Support Resources"
              value={libraryName}
              onChange={e => setLibraryName(e.target.value)}
            />
          </div>

          {/* Site browser */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-slate-600">Select SharePoint site</p>
              <button
                className="text-xs text-blue-600 hover:underline"
                onClick={loadSites}
                disabled={loadingSites}
              >
                {loadingSites ? "Loading..." : "Browse sites"}
              </button>
            </div>

            {sites.length > 0 && (
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-40 overflow-y-auto mb-3">
                {sites.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-slate-50 ${
                      selectedSite?.id === s.id ? "bg-blue-50 text-blue-700" : "text-slate-700"
                    }`}
                    onClick={() => selectSite(s)}
                  >
                    <span>{s.displayName || s.webUrl}</span>
                    {selectedSite?.id === s.id && <span className="text-xs text-blue-600">✓ selected</span>}
                  </button>
                ))}
              </div>
            )}

            {selectedSite && (
              <div className="mb-3">
                <p className="text-xs font-medium text-slate-600 mb-2">
                  Select document library in <em>{selectedSite.displayName}</em>
                </p>
                {loadingDrives ? (
                  <p className="text-xs text-slate-400">Loading libraries...</p>
                ) : drives.length === 0 ? (
                  <p className="text-xs text-slate-400">No document libraries found.</p>
                ) : (
                  <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-36 overflow-y-auto">
                    {drives.map(d => (
                      <button
                        key={d.id}
                        type="button"
                        className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-slate-50 ${
                          selectedDrive?.id === d.id ? "bg-blue-50 text-blue-700" : "text-slate-700"
                        }`}
                        onClick={() => setSelectedDrive(d)}
                      >
                        <span>{d.name}</span>
                        {selectedDrive?.id === d.id && <span className="text-xs text-blue-600">✓ selected</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Allow upload toggle */}
          <label className="flex items-center gap-3 mb-5 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              checked={allowUpload}
              onChange={e => setAllowUpload(e.target.checked)}
            />
            <div>
              <p className="text-sm text-slate-700">Allow agents to upload files to this library</p>
              <p className="text-xs text-slate-400">
                When off, agents can only browse and share links to existing files. When on, they can also upload from the chat window.
              </p>
            </div>
          </label>

          {saveMsg && (
            <p className={`text-xs rounded px-3 py-2 mb-4 ${saveMsg.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
              {saveMsg.ok ? "✓ " : "✗ "}{saveMsg.text}
            </p>
          )}

          <PrimaryBlueButton
            label={saving ? "Saving..." : "Save File Library"}
            onClick={save}
            disabled={saving || !selectedSite || !selectedDrive}
          />
        </div>
      )}

      {/* What agents see */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <p className="text-sm font-semibold text-slate-700 mb-3">What agents see in the chat window</p>
        <div className="space-y-2 text-xs text-slate-600">
          <p>• A file attachment button appears in the agent message bar (requires velaro-messaging workspace update)</p>
          <p>• Agents click it to open the file browser showing your configured library</p>
          <p>• They pick a file → Velaro generates a shareable link → link is pasted into the chat</p>
          <p>• Visitors can click the link to view the file without signing in (anonymous view by default)</p>
          {settings?.allowAgentUpload && (
            <p>• Agents can also upload new files from their computer directly into the library</p>
          )}
        </div>
      </div>
    </div>
  );
}

function HowItWorks() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 mb-4 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-xs font-medium text-slate-600 hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span>Why a shared library instead of personal OneDrive?</span>
        <span className="text-slate-400 ml-2">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 text-xs text-slate-600 space-y-2">
          <p className="mt-3">
            Personal OneDrive requires each agent to sign in with their Microsoft account —
            complex, requires per-user OAuth, and files aren't shared with the team.
          </p>
          <p>
            A shared SharePoint document library works with the same Microsoft 365 consent you've already given.
            Everyone on the team can access the same files, managers can organize the library, and Velaro
            generates sharing links without any per-agent setup.
          </p>
          <p className="text-slate-500">
            <strong>Tip:</strong> Create a dedicated "Velaro Resources" library in SharePoint for files agents frequently share
            (product sheets, pricing, how-to guides).
          </p>
        </div>
      )}
    </div>
  );
}
